import { OmniElement, OmniStyleElement, html, css, nothing } from 'omni-ui';
import { trackPortalEvent } from '../../helpers/utils.js';

OmniStyleElement.register();

/**
 * Class for Attention Calculator Multi Rec Score Module Element
 * @extends OmniElement
 */
export class CampaignAttnCalcMultiRecScore extends OmniElement {
  /**
   * Attention Calculator Legend Lit Properties
   * @property {Object} schema - Values passed in from wrapper/router
   * @property {Array} completedAttentionSteps - array of completed steps with each element representing the step number
   * @property {Number} recommendationSum - sum of filtered scores excluding n/a values
   * @property {Array} finalRecommendations - Scores, output, and time value of currently selected recommendation count
   * @property {Number} recommendationCount - Number of recommendations to be displayed
   * @property {Object} scores - unfiltered outputs and scores passed in by {@link CampaignAttnCalcMultiRec}
   */
  static get properties() {
    return {
      schema: { type: Object },
      completedAttentionSteps: { type: Array },
      recommendationSum: { type: Number },
      finalRecommendations: { type: Array },
      recommendationCount: { type: Number, reflect: true },
      scores: { type: Object },
      context: { type: Object },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .score-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-top: 2.143rem;
          margin-left: 2.857rem;
        }
        .score-divider {
          width: 1px;
          height: 5rem;
          align-self: center;
          background-color: rgb(206, 206, 206);
          margin-right: 2rem;
        }
        .attention-level {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-right: 2rem;
        }
        h1.attention-level-value {
          font-size: 2.571rem;
        }
        .score-value {
          flex-flow: column nowrap;
          align-items: flex-end;
        }
        .score-header {
          display: flex;
        }
        .omni h2.subtext {
          color: var(--color-almost-black);
          opacity: 0.5;
          margin-bottom: 2.143rem;
        }
        .omni hr {
          height: 1px;
          margin: 0.643rem 2.857rem 0;
        }
        .recommendations {
          display: flex;
          flex-wrap: wrap;
          margin-top: 1rem;
          justify-content: center;
          align-items: center;
        }
        .rec-column {
          display: flex;
          position: relative;
          flex: 0 0 calc(100% / 3 - 10px);
          margin: 5px;
          align-items: center;
          justify-content: center;
          min-width: 14.286rem;
        }
        .rec-column::after {
          position: absolute;
          content: '';
          right: -0.5px;
          top: 25%;
          bottom: 25%;
          width: 1px;
          background-color: rgba(var(--rgb-core-dark), 50%);
          /* hack to reduce lines of repetitive code below in media queries */
          display: none;
        }
        .whole-rec {
          display: flex;
          gap: 2.143rem;
        }
        .half-rec {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }
        @media screen and (min-width: 1261px) {
          .rec-column {
            flex: 1 1 auto;
          }
          .rec-column:not(:last-child)::after {
            display: block;
          }
        }
        /**
         * controls display of dividers between finalRecommendations as window shrinks
        */
        @media screen and (max-width: 1260px) and (min-width: 846px) {
          :host(:not([recommendationCount='2'], [recommendationCount='1']))
            .rec-column:not(:nth-child(2n + 3))::after,
          :host([recommendationCount='2']) .rec-column:not(:last-child)::after {
            display: block;
          }
        }
        @media screen and (max-width: 845px) {
          :host(:not([recommendationCount='2'], [recommendationCount='1']))
            .rec-column:not(:nth-child(2n), :last-child)::after,
          :host([recommendationCount='2']) .rec-column:not(:last-child)::after {
            display: block;
          }
        }
        .recommendation-container {
          display: flex;
          flex-direction: column;
          margin: 2.143rem 0 2.143rem 2.857rem;
          background: var(
            --omni-app-layout-bg,
            var(--omni-app-layout-bg-color, #f1f5fa)
          );
          min-height: 8.5rem;
          border-radius: 0.714rem;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
        }
      `,
    ];
  }

  updated(changedProperties) {
    if (
      this.completedAttentionSteps.length === this.schema?.steps?.length &&
      (changedProperties.has('recommendationCount') ||
        changedProperties.has('scores')) &&
      this.scores
    )
      this.#calculateColumns(this.recommendationCount);
  }

  /**
   * Calculates {@link recommendationSum} and outputs {@link finalRecommendations} for use in render
   * @param {number} columnCount - (Optional) Value associated with dropdown selection @change event
   * @fires CampaignAttnCalcMultiRecScore~event:recommendation-update
   */
  #calculateColumns = columnCount => {
    const columns = columnCount ?? this.recommendationCount;

    this.recommendationSum = Object.entries(this.scores).reduce(
      (acc, [key, value]) => (key === '0' ? acc : acc + value),
      0,
    );
    const recommendationScores = Object.entries(this.scores).reduce(
      (acc, [key, score]) => {
        if (key === '0') return acc;
        // eslint-disable-next-line no-param-reassign
        acc[key] = {
          value: (score / this.recommendationSum) * 100,
          text: key,
        };
        return acc;
      },
      {},
    );

    const { Low, MidLow, Mid, MidHigh, High } = recommendationScores;
    switch (columns) {
      case 5:
        this.finalRecommendations = [Low, MidLow, Mid, MidHigh, High];
        break;
      case 3:
        this.finalRecommendations = [
          { ...Low, value: Low.value + MidLow.value },
          Mid,
          { ...High, value: MidHigh.value + High.value },
        ];
        break;
      case 2:
        this.finalRecommendations = [
          { ...Low, value: Low.value + MidLow.value + Mid.value / 2 },
          { ...High, value: Mid.value / 2 + MidHigh.value + High.value },
        ];
        break;
      default: {
        const max = Math.max(
          Low.value,
          MidLow.value,
          Mid.value,
          MidHigh.value,
          High.value,
        );
        this.finalRecommendations = [
          // match the value of value key to the associated text value of same obj
          [Low, MidLow, Mid, MidHigh, High].find(ele => ele.value === max),
        ];
      }
    }
    if (columnCount)
      this.dispatchNewEvent('recommendation-update', {
        detail: columns,
      });
  };

  /**
   * @returns {html} Rendered HTML for the component
   */
  render() {
    const {
      score_title_text: scoreTitleText,
      score_subtitle_text: scoreSubtitleText,
      recommendation_schema: recommendationSchema,
      score_fine_print: scoreFinePrint,
    } = this.schema;
    return html` <omni-style>
      <hr style="height: 2px" />
      <div class="score-container">
        <div class="attention-level">
          <h1 class="title mb-1 is-size-4">${scoreTitleText}</h1>
          <h2 class="is-size-4 subtext">${scoreSubtitleText}</h2>
          <label class="subtitle mr-1 mb-2 question-label"
            >${'Recommended attention levels' ?? ''}</label
          >
          <div>
            <om2-dropdown
              placeholder="Select Recommendation"
              .options=${Object.values(recommendationSchema)}
              .value=${this.recommendationCount}
              @change=${e => {
                let portalMessage;
                switch (e.target.value) {
                  case 5:
                    portalMessage = '5 part solution';
                    break;
                  case 3:
                    portalMessage = '3 part solution';
                    break;
                  case 2:
                    portalMessage = '2 part solution';
                    break;
                  default:
                    portalMessage = 'recommended solution';
                }
                e.preventDefault();
                this.#calculateColumns(e.target.value);
                trackPortalEvent(
                  portalMessage,
                  'open',
                  'multi rec attention calculator module',
                );
              }}
              ?disabled=${this.completedAttentionSteps.length !==
                this.schema?.steps?.length || this.context?.readonly}
            ></om2-dropdown>
          </div>
        </div>
      </div>
      <div class="recommendation-container">
        <div class="recommendations">
          ${this.recommendationCount &&
          this.completedAttentionSteps.length === this.schema?.steps?.length
            ? this.finalRecommendations?.map(
                rec => html`
                  <div class="rec-column">
                    <div class="whole-rec">
                      <div class="half-rec">
                        <div class="is-size-2 has-text-weight-bold">
                          ${recommendationSchema[this.recommendationCount]
                            .times[rec.text]}s
                        </div>
                        <div class="is-size-7">
                          ${rec.text.replace(/([a-z])([A-Z])/g, '$1-$2')}
                        </div>
                      </div>
                      ${this.recommendationCount !== 1
                        ? html`<div class="half-rec">
                            <div class="is-size-2 has-text-weight-bold">
                              ${rec.value.toFixed(1)}%
                            </div>
                            <div class="is-size-7">% of Budget</div>
                          </div>`
                        : ''}
                    </div>
                  </div>
                `,
              )
            : html`
                <div class="rec-column">
                  <div class="whole-rec">
                    <div class="half-rec">
                      <div class="is-size-2 has-text-weight-bold">-s</div>
                      <div class="is-size-7">Attention Level</div>
                    </div>
                    <div class="half-rec">
                      <div class="is-size-2 has-text-weight-bold">-%</div>
                      <div class="is-size-7">% of Budget</div>
                    </div>
                  </div>
                </div>
              `}
        </div>
        ${this.recommendationCount !== 1 &&
        this.completedAttentionSteps.length === this.schema?.steps?.length
          ? html` <div class="mx-5 my-3">${scoreFinePrint}</div>`
          : nothing}
      </div>
    </omni-style>`;
  }
}

OmniElement.register(
  'campaign-attn-calc-multi-rec-score',
  CampaignAttnCalcMultiRecScore,
);
